<template>
  <div class="login-wrap">
    <div class="ms-login">
      <div class="ms-title">后台管理系统</div>
      <el-form
        v-if="viewState === 'login'"
        :model="ruleForm"
        ref="ruleForm"
        label-width="0px"
        class="ms-content"
      >
        <el-form-item prop="username">
          <el-input v-model="ruleForm.username" placeholder="用户名">
            <el-button slot="prepend" icon="el-icon-user" disabled></el-button>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <div @keydown.enter="toLogin">
            <el-input
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="密码"
              v-model="ruleForm.password"
            >
              <el-button slot="prepend" icon="el-icon-lock" disabled></el-button>
              <i
                slot="suffix"
                class="el-input__icon el-icon-view password-toggle"
                :class="{ 'is-visible': passwordVisible }"
                role="button"
                tabindex="0"
                :aria-label="passwordVisible ? '隐藏密码' : '显示密码'"
                :title="passwordVisible ? '隐藏密码' : '显示密码'"
                @mousedown.prevent
                @click="passwordVisible = !passwordVisible"
                @keydown.enter.stop.prevent="passwordVisible = !passwordVisible"
                @keydown.space.stop.prevent="passwordVisible = !passwordVisible"
              ></i>
            </el-input>
          </div>
        </el-form-item>
        <div class="login-btn">
          <el-button type="primary" :loading="loggingIn" @click="toLogin">登录</el-button>
        </div>
      </el-form>
      <div v-else class="approval-content">
        <i class="el-icon-mobile-phone approval-icon"></i>
        <h3>当前设备需要授权</h3>
        <p class="approval-tip">请联系系统管理员，并提供以下申请码</p>
        <div class="approval-code">{{ approval.displayCode }}</div>
        <p class="approval-status">{{ approvalStatus }}</p>
        <p class="approval-countdown">
          申请将在 {{ approval.remainingSeconds }} 秒后失效
        </p>
        <el-button type="text" @click="cancelApproval">返回重新登录</el-button>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    createDeviceTrustRequest,
    isUnifiedDeviceTrustClient,
    saveLoginSession,
    savePendingDeviceTrust
  } from '@/utils/deviceTrust';

  const APPROVAL_REQUIRED = 671001;
  const UNIFIED_AUTHORIZATION_REQUIRED = 671101;
  const APPROVAL_PENDING = 671002;
  const APPROVAL_REJECTED = 671003;
  const APPROVAL_EXPIRED = 671004;
  const DEVICE_LIMIT_REACHED = 671005;
  const INVALID_APPROVAL = 671007;
  const APPROVAL_REQUEST_KEY = 'erpDeviceApprovalRequestId';
  const APPROVAL_CODE_KEY = 'erpDeviceApprovalDisplayCode';
  const APPROVAL_EXPIRES_KEY = 'erpDeviceApprovalExpiresAt';

  export default {
    data: function () {
      return {
        ruleForm: {
          username: '',
          password: ''
        },
        passwordVisible: false,
        viewState: 'login',
        loggingIn: false,
        approvalPolling: false,
        approvalStatus: '等待管理员审批…',
        approvalTimer: null,
        approval: {
          requestId: '',
          displayCode: '',
          expiresAt: '',
          remainingSeconds: 0
        },
      }
    },
    created() {
      localStorage.removeItem('browserFingerprint');
      this.restoreApproval();
    },
    beforeDestroy() {
      this.stopApprovalPolling();
    },
    beforeRouteEnter(to, from, next) {
      to.params.params = from.params;
      to.params.query = from.query;
      to.params.name = from.name;
      to.params.fullPath = from.fullPath;
      next();
    },
    methods: {
      toLogin() {
        if (this.loggingIn) return;
        if (!this.checkUsername(this.ruleForm.username) || !this.checkPassword(this.ruleForm.password)) return;
        this.loggingIn = true;
        let deviceTrustRequest = null;
        const prepareRequest = isUnifiedDeviceTrustClient()
          ? createDeviceTrustRequest()
          : Promise.resolve(null);
        return prepareRequest
          .then(request => {
            deviceTrustRequest = request;
            const loginForm = {
              username: this.ruleForm.username,
              password: this.ruleForm.password,
              city: sessionStorage.getItem('city')
            };
            if (request) {
              loginForm.deviceTrust = {
                state: request.state,
                codeChallenge: request.codeChallenge
              };
            }
            return this.$axios.post(this.$device_auth_url_prefix + 'login', loginForm, {
              withCredentials: true
            });
          })
          .then(res => {
            if (res.data.success) {
              this.finishLogin(res.data.data);
            } else if (res.data.code === APPROVAL_REQUIRED) {
              this.beginApproval(res.data.data);
            } else if (res.data.code === UNIFIED_AUTHORIZATION_REQUIRED && deviceTrustRequest) {
              this.beginUnifiedAuthorization(res.data.data, deviceTrustRequest);
            } else {
              this.$message.error(res.data.msg || '用户名或密码错误');
            }
          })
          .catch(err => {
            this.$message.error(err.message || '登录失败，请检查网络');
            console.log('login fail', err);
          })
          .finally(() => {
            this.loggingIn = false;
          });
      },
      beginUnifiedAuthorization(data, request) {
        this.ruleForm.password = '';
        const routeParams = this.$route.params || {};
        const returnRoute = routeParams.name
          ? {
            name: routeParams.name,
            params: routeParams.params,
            query: routeParams.query
          }
          : {
            path: '/'
          };
        savePendingDeviceTrust(request, data.expiresAt, returnRoute);
        window.location.assign(data.authorizeUrl);
      },
      beginApproval(data) {
        this.ruleForm.password = '';
        this.approval = {
          requestId: data.requestId,
          displayCode: data.displayCode,
          expiresAt: data.expiresAt,
          remainingSeconds: 0
        };
        sessionStorage.setItem(APPROVAL_REQUEST_KEY, data.requestId);
        sessionStorage.setItem(APPROVAL_CODE_KEY, data.displayCode);
        sessionStorage.setItem(APPROVAL_EXPIRES_KEY, data.expiresAt);
        this.viewState = 'approval';
        this.approvalStatus = '等待管理员审批…';
        this.startApprovalPolling();
      },
      restoreApproval() {
        const requestId = sessionStorage.getItem(APPROVAL_REQUEST_KEY);
        const displayCode = sessionStorage.getItem(APPROVAL_CODE_KEY);
        const expiresAt = sessionStorage.getItem(APPROVAL_EXPIRES_KEY);
        if (!requestId || !expiresAt || new Date(expiresAt).getTime() <= Date.now()) {
          this.clearApprovalStorage();
          return;
        }
        this.approval = {
          requestId,
          displayCode: displayCode || '',
          expiresAt,
          remainingSeconds: 0
        };
        this.viewState = 'approval';
        this.startApprovalPolling();
      },
      startApprovalPolling() {
        this.stopApprovalPolling();
        this.updateApprovalCountdown();
        if (this.viewState !== 'approval') return;
        this.pollApproval();
        this.approvalTimer = window.setInterval(() => {
          this.updateApprovalCountdown();
          if (this.approval.remainingSeconds > 0) {
            this.pollApproval();
          }
        }, 3000);
      },
      stopApprovalPolling() {
        if (this.approvalTimer) {
          window.clearInterval(this.approvalTimer);
          this.approvalTimer = null;
        }
      },
      updateApprovalCountdown() {
        const remaining = Math.ceil((new Date(this.approval.expiresAt).getTime() - Date.now()) / 1000);
        this.approval.remainingSeconds = Math.max(0, remaining);
        if (remaining <= 0) {
          this.handleApprovalTerminal('设备授权申请已过期，请重新登录');
        }
      },
      pollApproval() {
        if (!this.approval.requestId || this.approvalPolling) return;
        this.approvalPolling = true;
        return this.$axios.post(this.$device_auth_url_prefix + 'device-approval/status', {
          requestId: this.approval.requestId
        }, {
          withCredentials: true
        }).then(res => {
          if (res.data.success) {
            this.approvalStatus = '授权成功，正在登录…';
            this.finishLogin(res.data.data);
            return;
          }
          if (res.data.code === APPROVAL_PENDING) {
            this.approvalStatus = '等待管理员审批…';
            return;
          }
          if ([APPROVAL_REJECTED, APPROVAL_EXPIRED, DEVICE_LIMIT_REACHED, INVALID_APPROVAL].includes(res.data.code)) {
            this.handleApprovalTerminal(res.data.msg || '设备授权申请已失效');
          }
        }).catch(err => {
          this.approvalStatus = '网络异常，正在重试…';
          console.log('poll approval fail', err);
        }).finally(() => {
          this.approvalPolling = false;
        });
      },
      handleApprovalTerminal(message) {
        this.stopApprovalPolling();
        this.clearApprovalStorage();
        this.viewState = 'login';
        this.$message.error(message);
      },
      cancelApproval() {
        this.stopApprovalPolling();
        this.clearApprovalStorage();
        this.viewState = 'login';
      },
      clearApprovalStorage() {
        sessionStorage.removeItem(APPROVAL_REQUEST_KEY);
        sessionStorage.removeItem(APPROVAL_CODE_KEY);
        sessionStorage.removeItem(APPROVAL_EXPIRES_KEY);
      },
      finishLogin(data) {
        this.stopApprovalPolling();
        this.clearApprovalStorage();
        const sessionid = data.sessionid;
        if (!sessionid) {
          this.$message.error('登录返回数据无效');
          return;
        }
        saveLoginSession(this.$store, data);
        this.loginZuke();

        const routeParams = this.$route.params;
        let toObj = {
          path: '/'
        };
        if (routeParams.name) {
          toObj = {
            name: routeParams.name,
            params: routeParams.params,
            query: routeParams.query
          };
        }
        this.$router.replace(toObj);
      },
      loginZuke() {
        this.$axios({
          url: this.$zhke_url + 'member/login/isUser',
          method: 'get',
          params: {
            code: 'Ra7Z5cmbyxzzYaM'
          }
        }).then(res => {
          if (res.data.success) {
            sessionStorage.setItem('zukeToken', res.data.data);
          }
        }).catch(err => {
          console.log(err, 'err');
        });
      },
      checkUsername(username) {
        if (username && username.trim()) return true;
        this.$message.warning('请输入用户名');
        return false;
      },
      checkPassword(password) {
        if (password) return true;
        this.$message.warning('请输入密码');
        return false;
      }

    }
  }

</script>

<style scoped>
  .login-wrap {
    position: fixed;
    top: 0;
    left: 0;
    overflow: hidden;
    width: 100%;
    height: 100%;
    background-image: url(../assets/login-bg.jpg);
    background-size: 100%;
  }

  .ms-title {
    width: 100%;
    line-height: 50px;
    text-align: center;
    font-size: 20px;
    color: #fff;
    border-bottom: 1px solid #ddd;
  }

  .ms-login {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 350px;
    margin: -190px 0 0 -175px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.3);
    overflow: hidden;
  }

  .ms-content {
    padding: 30px 30px;
  }

  .login-btn {
    text-align: center;
  }

  .password-toggle {
    cursor: pointer;
    color: #c0c4cc;
  }

  .password-toggle:hover,
  .password-toggle:focus,
  .password-toggle.is-visible {
    color: #409eff;
    outline: none;
  }

  .login-btn button {
    width: 100%;
    height: 36px;
    margin-bottom: 10px;
  }

  .approval-content {
    padding: 28px 30px 24px;
    text-align: center;
    color: #fff;
  }

  .approval-content h3 {
    margin: 10px 0;
    font-size: 18px;
  }

  .approval-icon {
    font-size: 38px;
  }

  .approval-tip,
  .approval-status,
  .approval-countdown {
    margin: 8px 0;
    font-size: 13px;
  }

  .approval-code {
    margin: 18px 0;
    font-size: 34px;
    font-weight: bold;
    letter-spacing: 8px;
  }

  .approval-content .el-button {
    color: #fff;
  }

</style>



// WEBPACK FOOTER //
// src/page/Login.vue